import { FC } from 'react'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material'

import { NAV_ITEM_RADIUS_PX, NAV_RAIL_ITEM_WIDTH_PX } from '@/common/constants'
import {
  getImpersonatedUserEmail,
  stopImpersonatingUser,
} from '@/modules/authentication/impersonation'

const CARD_SX = {
  backgroundColor: '#ffb5b5',
  borderRadius: `${NAV_ITEM_RADIUS_PX}px`,
}

interface ImpersonatedUserBannerMUIProps {
  open: boolean
}

const ImpersonatedUserBannerMUI: FC<ImpersonatedUserBannerMUIProps> = ({ open }) => {
  const impersonatedUserEmail = getImpersonatedUserEmail()

  if (!impersonatedUserEmail) return null

  // Collapsed, the card is an icon tile the same size as the nav icons above it. Only the
  // tooltip shows the email, so the tile takes keyboard focus to let keyboard users open it.
  if (!open)
    return (
      <Tooltip title={`Viewing as ${impersonatedUserEmail}`} placement="right">
        <Paper
          elevation={2}
          tabIndex={0}
          sx={{
            ...CARD_SX,
            width: `${NAV_RAIL_ITEM_WIDTH_PX}px`,
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'common.white',
              outlineOffset: 2,
            },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </Paper>
      </Tooltip>
    )

  // Open, the card has the same side margins as the nav items, and the same space on every
  // side of its content.
  return (
    <Paper elevation={2} sx={{ ...CARD_SX, alignSelf: 'stretch', mx: 2, p: 1.5 }}>
      <Stack spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" component="p">
            Viewing as:
          </Typography>
          <Typography variant="body1" noWrap title={impersonatedUserEmail}>
            {impersonatedUserEmail}
          </Typography>
        </Box>
        <Button
          fullWidth
          size="small"
          variant="contained"
          color="error"
          onClick={() => {
            stopImpersonatingUser()
            window.location.reload()
          }}
        >
          End Session
        </Button>
      </Stack>
    </Paper>
  )
}

export default ImpersonatedUserBannerMUI
